const GOOGLE_STORAGE_URL_PATTERN =
  /^https?:\/\/(?:storage\.googleapis\.com|firebasestorage\.googleapis\.com)\//i;

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif"];
const PDF_EXTENSIONS = [".pdf"];

const inferMimeTypeFromFileName = (fileName) => {
  const lower = (fileName || "").toLowerCase();

  if (lower.endsWith(".pdf")) {
    return "application/pdf";
  }
  if (lower.endsWith(".png")) {
    return "image/png";
  }
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lower.endsWith(".webp")) {
    return "image/webp";
  }
  if (lower.endsWith(".gif")) {
    return "image/gif";
  }

  return undefined;
};

export function isGoogleStorageUrl(url) {
  if (!url || typeof url !== "string") {
    return false;
  }

  return GOOGLE_STORAGE_URL_PATTERN.test(url.trim());
}

function getFileExtensionFromUrl(url) {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split("/").pop() || "";

    return lastSegment.includes(".") ? lastSegment.slice(lastSegment.lastIndexOf(".")).toLowerCase() : "";
  } catch {
    const withoutQuery = url.split("?")[0] || "";
    const lastSegment = withoutQuery.split("/").pop() || "";

    return lastSegment.includes(".") ? lastSegment.slice(lastSegment.lastIndexOf(".")).toLowerCase() : "";
  }
}

function getFileType({ name, mimeType }) {
  const normalizedMimeType = (mimeType || "").toLowerCase();

  if (normalizedMimeType.startsWith("image/")) {
    return "image";
  }
  if (normalizedMimeType === "application/pdf") {
    return "pdf";
  }

  const normalizedName = (name || "").toLowerCase();
  const extensionFromUrl = getFileExtensionFromUrl(name);

  if (
    IMAGE_EXTENSIONS.some((ext) => normalizedName.endsWith(ext) || extensionFromUrl === ext)
  ) {
    return "image";
  }
  if (
    PDF_EXTENSIONS.some((ext) => normalizedName.endsWith(ext) || extensionFromUrl === ext)
  ) {
    return "pdf";
  }

  return "unsupported";
}

export function canPreviewFile(file) {
  if (!file?.downloadURL) {
    return false;
  }

  return getFileType({ name: file.name || "", mimeType: file.mimeType }) !== "unsupported";
}

export function isPreviewableDocumentUrl(url) {
  if (!url || typeof url !== "string") {
    return false;
  }

  const trimmedUrl = url.trim();
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return false;
  }

  if (trimmedUrl.startsWith("navigate:")) {
    return false;
  }

  const extension = getFileExtensionFromUrl(trimmedUrl);
  const hasPreviewableExtension =
    PDF_EXTENSIONS.includes(extension) || IMAGE_EXTENSIONS.includes(extension);

  if (hasPreviewableExtension) {
    return true;
  }

  return isGoogleStorageUrl(trimmedUrl);
}

function extractFileNameFromUrl(url) {
  if (!url) {
    return "Document";
  }

  try {
    const pathname = new URL(url).pathname;
    const lastSegment = decodeURIComponent(pathname.split("/").pop() || "");

    return lastSegment || "Document";
  } catch {
    const withoutQuery = url.split("?")[0] || "";
    const lastSegment = decodeURIComponent(withoutQuery.split("/").pop() || "");

    return lastSegment || "Document";
  }
}

export function buildPreviewFileFromLink(href, label) {
  const downloadURL = (href || "").trim();
  const cleanedLabel = typeof label === "string" ? label.trim() : "";
  const name = cleanedLabel || extractFileNameFromUrl(downloadURL);
  const mimeType =
    inferMimeTypeFromFileName(name) || inferMimeTypeFromFileName(extractFileNameFromUrl(downloadURL));

  return {
    name,
    downloadURL,
    mimeType,
  };
}

export function canPreviewDocumentLink(href, label) {
  if (isPreviewableDocumentUrl(href)) {
    return true;
  }

  return canPreviewFile(buildPreviewFileFromLink(href, label));
}
